import { useEffect, useState } from 'react'
import { Paperclip } from 'lucide-react'
import { Pagination } from '../../../../components/Pagination'
import type { LeavePolicyDto } from '../types/hrmsLeavePolicies.types'

interface LeavePoliciesTableProps {
  policies: LeavePolicyDto[]
  totalAssignedPersonnel: number
  onEditLeavePolicy: (id: string) => void
}

function getPageNumbers(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  if (currentPage <= 3) {
    return [1, 2, 3, 4, 'ellipsis', totalPages]
  }
  if (currentPage >= totalPages - 2) {
    return [1, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
  }
  return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages]
}

export function LeavePoliciesTable({
  policies,
  totalAssignedPersonnel,
  onEditLeavePolicy,
}: LeavePoliciesTableProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 5
  const totalCount = policies.length
  const totalPages = Math.ceil(totalCount / pageSize)

  useEffect(() => {
    setCurrentPage(1)
  }, [policies])

  const from = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const to = Math.min(currentPage * pageSize, totalCount)

  const displayedPolicies = policies.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden min-w-0 font-sans">
      <div className="overflow-x-auto min-w-0">
        <table className="w-full min-w-max table-auto text-left text-xs">
          <thead>
            <tr className="bg-gray-100 border-b border-slate-200 text-[11px] font-semibold uppercase text-gray-500 tracking-wide">
              <th className="px-4 py-3 whitespace-nowrap">POLICY NAME</th>
              <th className="px-4 py-3 whitespace-nowrap">EMPLOYEE GROUP</th>
              <th className="px-4 py-3 whitespace-nowrap">ENTITLEMENT</th>
              <th className="px-4 py-3 whitespace-nowrap">CARRY FORWARD</th>
              <th className="px-4 py-3 whitespace-nowrap">STATUS</th>
              <th className="px-4 py-3 whitespace-nowrap text-right">ACTION</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-slate-700">
            {displayedPolicies.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-500 text-sm font-medium">
                  No leave policies found
                </td>
              </tr>
            ) : (
              displayedPolicies.map((policy) => {
                const isActive =
                  policy.status?.toUpperCase() === 'ACTIVE' || policy.status === 'Active'
                const staffTypeName =
                  policy.staffType?.displayName || policy.staffTypeId || policy.employeeGroup || '—'

                return (
                  <tr key={policy.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-3 font-bold text-slate-900 whitespace-nowrap">
                      <div className="flex flex-col gap-0.5">
                        <span>{typeof policy.name === 'string' ? policy.name : String(policy.name || '')}</span>
                        {policy.attachmentUrl && (
                          <a
                            href={typeof policy.attachmentUrl === 'string' ? policy.attachmentUrl : '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-normal text-teal-700 hover:text-teal-900 hover:underline"
                            title={typeof policy.attachmentName === 'string' ? policy.attachmentName : 'View Attachment'}
                          >
                            <Paperclip className="h-3 w-3 shrink-0" />
                            <span className="truncate max-w-[180px]">
                              {typeof policy.attachmentName === 'string' ? policy.attachmentName : 'View Attachment'}
                            </span>
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {typeof staffTypeName === 'string' ? staffTypeName : '—'}
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-900 whitespace-nowrap">
                      {policy.annualEntitlementDays ?? policy.entitlementDays ?? 0} days / year
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(policy.allowCarryForward ?? ((policy.carryForwardDays ?? 0) > 0)) &&
                      (policy.maxCarryForwardDays ?? policy.carryForwardDays ?? 0) > 0 ? (
                        `Up to ${policy.maxCarryForwardDays ?? policy.carryForwardDays} days`
                      ) : (
                        <span className="text-slate-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 rounded-full px-2.5 py-1 text-xs font-medium border border-green-200/80 whitespace-nowrap">
                          <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-600 rounded-full px-2.5 py-1 text-xs font-medium border border-gray-200 whitespace-nowrap">
                          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onEditLeavePolicy(policy.id)}
                        className="text-[#146971] hover:underline font-semibold text-xs cursor-pointer whitespace-nowrap"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="bg-white px-4 py-3 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div>
              Showing <strong className="font-semibold text-slate-800">{from}–{to}</strong> of{' '}
              <strong className="font-semibold text-slate-800">{totalCount}</strong> policies
            </div>
            <span className="hidden sm:inline text-slate-300">•</span>
            <div>
              Total Assigned Personnel:{' '}
              <strong className="font-bold text-slate-900">{totalAssignedPersonnel || 486} Staff Members</strong>
            </div>
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pages={getPageNumbers(currentPage, totalPages)}
              onPageChange={(p) => setCurrentPage(p)}
              onPrevious={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              onNext={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              previousDisabled={currentPage === 1}
              nextDisabled={currentPage === totalPages}
            />
          )}
        </div>
      )}
    </div>
  )
}
